import { Ban, CheckCircle2, Loader2, RotateCcw, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatCurrencyWithCommas } from "../../../utils/numberUtils";
import {
  EXCLUSION_EXPLAINER_NLV,
  EXCLUSION_PHASE_LABELS,
  EXCLUSION_PHASE_ORDER,
  EXCLUSION_SYMBOL,
  EXCLUSION_TARGET_PCT,
  formatExclusionTradeLabel,
  formatTargetPct,
  getExclusionExplainerSnapshot,
  type ExclusionExplainerMode,
  type ExclusionExplainerPhase,
} from "./contractExclusionsExplainerDemo";

interface ContractExclusionsExplainerModalProps {
  isOpen: boolean;
  onClose: () => void;
  appearance?: "default" | "alpha";
}

type FilterCheckStage =
  | "idle"
  | "checking"
  | "result"
  | "revealed-0"
  | "revealed-1"
  | "revealed-2"
  | "done";

const FILTER_CHECK_DELAY_MS = 850;
const FILTER_RESULT_DELAY_MS = 650;
const FILTER_ROW_DELAY_MS = 450;

function isFilterRowRevealed(stage: FilterCheckStage, rowIndex: number): boolean {
  if (stage === "done") return true;
  if (stage === "revealed-0") return rowIndex === 0;
  if (stage === "revealed-1") return rowIndex <= 1;
  if (stage === "revealed-2") return rowIndex <= 2;
  return false;
}

function getFilterBannerText(mode: ExclusionExplainerMode, stage: FilterCheckStage): string {
  if (stage === "checking") return "Checking for exclusions...";
  if (stage === "result" || stage === "idle") {
    return mode === "excluded"
      ? `1 exclusion found — ${EXCLUSION_SYMBOL}`
      : "No exclusions found";
  }
  if (stage === "done") {
    return mode === "excluded"
      ? `${EXCLUSION_SYMBOL} is on your exclusion list, so its planned trade is skipped.`
      : "All symbols cleared — planned trades would run.";
  }
  return mode === "excluded"
    ? `Reviewing planned trades against your exclusion list...`
    : "Reviewing planned trades...";
}

const ContractExclusionsExplainerModal: React.FC<ContractExclusionsExplainerModalProps> = ({
  isOpen,
  onClose,
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const [mode, setMode] = useState<ExclusionExplainerMode>("none");
  const [phase, setPhase] = useState<ExclusionExplainerPhase>("start");
  const [filterStage, setFilterStage] = useState<FilterCheckStage>("idle");

  const snapshot = useMemo(() => getExclusionExplainerSnapshot(mode, phase), [mode, phase]);
  const activeStepIndex = EXCLUSION_PHASE_ORDER.indexOf(phase);
  const isFilterPhase = phase === "filter";

  const resetToStart = useCallback(() => {
    setPhase("start");
    setFilterStage("idle");
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    resetToStart();
  }, [isOpen, resetToStart]);

  useEffect(() => {
    resetToStart();
  }, [mode, resetToStart]);

  useEffect(() => {
    if (phase !== "filter") {
      setFilterStage("idle");
      return;
    }

    setFilterStage("checking");
    const timers = [
      window.setTimeout(() => setFilterStage("result"), FILTER_CHECK_DELAY_MS),
      window.setTimeout(
        () => setFilterStage("revealed-0"),
        FILTER_CHECK_DELAY_MS + FILTER_RESULT_DELAY_MS,
      ),
      window.setTimeout(
        () => setFilterStage("revealed-1"),
        FILTER_CHECK_DELAY_MS + FILTER_RESULT_DELAY_MS + FILTER_ROW_DELAY_MS,
      ),
      window.setTimeout(
        () => setFilterStage("revealed-2"),
        FILTER_CHECK_DELAY_MS + FILTER_RESULT_DELAY_MS + FILTER_ROW_DELAY_MS * 2,
      ),
      window.setTimeout(
        () => setFilterStage("done"),
        FILTER_CHECK_DELAY_MS + FILTER_RESULT_DELAY_MS + FILTER_ROW_DELAY_MS * 3,
      ),
    ];

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [phase, mode]);

  if (!isOpen) return null;

  const microLabelClass = isAlpha
    ? "text-[10px] font-semibold uppercase tracking-wider text-v7-ink/55"
    : "text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400";
  const cardClass = isAlpha
    ? "rounded-v7-panel border border-v7-ink/15 bg-v7-white"
    : "rounded-lg border border-gray-200 bg-white dark:border-rp-separator dark:bg-black";
  const strongTextClass = isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white";
  const bodyTextClass = isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300";

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 ${isAlpha ? "bg-v7-ink/60" : "bg-black/70"}`}>
      <div
        data-appearance={appearance}
        className={`flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden border shadow-2xl ${
          isAlpha
            ? "rounded-v7-popover border-v7-ink/15 bg-v7-white font-v7-sans"
            : "rounded-xl border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface-raised"
        }`}
      >
        <div className={`flex items-start justify-between border-b px-5 py-4 ${isAlpha ? "border-v7-ink/10" : "border-gray-200 dark:border-rp-separator"}`}>
          <div>
            <h2 className={`text-base font-bold leading-tight ${strongTextClass}`}>
              How contract exclusions work
            </h2>
            <p className={`mt-1 text-sm ${bodyTextClass}`}>
              Simple example: a three-stock strategy targeting one-third each, with AAPL overweight before a rebalance.
            </p>
            <p className={`mt-2 text-xs ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
              Click each step below to walk through the example at your own pace.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={isAlpha
              ? "rounded-v7-control p-1 text-v7-ink/45 transition-colors hover:bg-v7-paper hover:text-v7-ink"
              : "rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-rp-surface dark:hover:text-white"}
            aria-label="Close explainer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className={`inline-flex border p-0.5 ${isAlpha ? "rounded-v7-control border-v7-ink/20 bg-v7-white" : "rounded-lg border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface"}`}>
              {(["none", "excluded"] as ExclusionExplainerMode[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setMode(option)}
                  className={
                    mode === option
                      ? isAlpha
                        ? "inline-flex items-center gap-1.5 rounded-v7-control bg-v7-bronze/10 px-3 py-1.5 text-xs font-semibold text-v7-bronze-dark transition-colors"
                        : "inline-flex items-center gap-1.5 rounded-md bg-rp-amber px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                      : isAlpha
                        ? "inline-flex items-center gap-1.5 rounded-v7-control px-3 py-1.5 text-xs font-semibold text-v7-ink-soft transition-colors hover:text-v7-ink"
                        : "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }
                >
                  {option === "excluded" && <Ban className="h-3.5 w-3.5" />}
                  {option === "none" ? "No exclusions" : `${EXCLUSION_SYMBOL} excluded`}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={resetToStart}
              className={isAlpha
                ? "inline-flex items-center gap-2 rounded-v7-control border border-v7-ink/25 bg-v7-white px-3 py-1.5 text-xs font-semibold text-v7-ink-soft transition-colors hover:border-v7-ink"
                : "inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Back to step 1
            </button>
          </div>

          <div className={`relative overflow-hidden border p-5 ${isAlpha ? "rounded-v7-panel border-v7-ink/15 bg-v7-paper" : "rounded-2xl border-gray-200 bg-gray-50 dark:border-rp-separator dark:bg-rp-surface-sunken"}`}>
            <div className={`pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full blur-2xl ${isAlpha ? "bg-v7-bronze/10" : "bg-rp-amber/10"}`} />
            <div className={`pointer-events-none absolute -bottom-10 -left-6 h-28 w-28 rounded-full blur-2xl ${isAlpha ? "bg-v7-rust/10" : "bg-red-500/10"}`} />

            <div className="relative space-y-5">
              <div className={`px-4 py-3 ${cardClass}`}>
                <p className={microLabelClass}>
                  Your target split
                </p>
                <p className={`mt-1 text-sm font-semibold ${strongTextClass}`}>
                  AAPL, MSFT, and GOOG at about {formatTargetPct(EXCLUSION_TARGET_PCT)} each
                </p>
                <p className={`mt-1 text-xs ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
                  Excluded symbols are never bought or sold by automated trading.
                </p>
              </div>

              <div>
                <p className={microLabelClass}>
                  Total account value
                </p>
                <p className={`mt-1 font-mono text-3xl font-bold tabular-nums ${strongTextClass}`}>
                  {formatCurrencyWithCommas(EXCLUSION_EXPLAINER_NLV, 0)}
                </p>
              </div>

              <div className={`relative overflow-hidden border ${isAlpha ? "rounded-v7-panel border-v7-ink/15 bg-v7-white" : "rounded-xl border-gray-200 bg-white dark:border-rp-separator dark:bg-black"}`}>
                {isFilterPhase && (
                  <div className={`border-b px-4 py-3 ${isAlpha ? "border-v7-ink/10" : "border-gray-200 dark:border-rp-separator"}`}>
                    <div className={`flex items-center gap-2 text-sm font-medium ${isAlpha ? "text-v7-ink-soft" : "text-gray-700 dark:text-gray-200"}`}>
                      {filterStage === "checking" ? (
                        <Loader2 className={`h-4 w-4 animate-spin ${isAlpha ? "text-v7-bronze-dark" : "text-rp-amber"}`} aria-hidden="true" />
                      ) : filterStage === "result" ? (
                        mode === "excluded" ? (
                          <Ban className={`h-4 w-4 ${isAlpha ? "text-v7-rust" : "text-red-500"}`} aria-hidden="true" />
                        ) : (
                          <CheckCircle2 className={`h-4 w-4 ${isAlpha ? "text-v7-success" : "text-green-500"}`} aria-hidden="true" />
                        )
                      ) : filterStage.startsWith("revealed") || filterStage === "done" ? (
                        mode === "excluded" ? (
                          <Ban className={`h-4 w-4 ${isAlpha ? "text-v7-rust" : "text-red-500"}`} aria-hidden="true" />
                        ) : (
                          <CheckCircle2 className={`h-4 w-4 ${isAlpha ? "text-v7-success" : "text-green-500"}`} aria-hidden="true" />
                        )
                      ) : null}
                      <span>{getFilterBannerText(mode, filterStage)}</span>
                    </div>
                  </div>
                )}
                <div className={`divide-y ${isAlpha ? "divide-v7-ink/10" : "divide-gray-200 dark:divide-rp-separator"}`}>
                  {snapshot.stocks.map((stock, rowIndex) => {
                    const rowRevealed = isFilterPhase && isFilterRowRevealed(filterStage, rowIndex);
                    const rowExcluded = mode === "excluded" && stock.symbol === EXCLUSION_SYMBOL;

                    return (
                      <div
                        key={stock.symbol}
                        className={`flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between transition-colors duration-300 ${
                          rowRevealed && rowExcluded
                            ? isAlpha ? "bg-v7-rust/5" : "bg-red-50/70 dark:bg-red-900/10"
                            : ""
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {isFilterPhase && (
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                              {!rowRevealed ? (
                                <span className={`h-2 w-2 rounded-full ${isAlpha ? "bg-v7-ink/30" : "bg-gray-300 dark:bg-gray-600"}`} />
                              ) : rowExcluded ? (
                                <Ban className={`h-5 w-5 ${isAlpha ? "text-v7-rust" : "text-red-500"}`} aria-label={`${stock.symbol} excluded`} />
                              ) : (
                                <CheckCircle2 className={`exclusion-explainer-check h-5 w-5 ${isAlpha ? "text-v7-success" : "text-green-500"}`} aria-label={`${stock.symbol} cleared`} />
                              )}
                            </span>
                          )}
                          {stock.excluded && phase === "complete" && (
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                              isAlpha
                                ? "border-v7-rust/30 bg-v7-rust/10 text-v7-rust"
                                : "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
                            }`}>
                              <Ban className="h-3 w-3" />
                              Excluded
                            </span>
                          )}
                          <div>
                            <p className={`text-sm font-semibold ${strongTextClass}`}>{stock.symbol}</p>
                            <p className={`font-mono text-xs tabular-nums ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
                              {formatCurrencyWithCommas(stock.dollars, 0)} · {formatTargetPct(stock.currentPct)} now · {formatTargetPct(stock.targetPct)} target
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs font-semibold uppercase tracking-wider transition-opacity duration-300 ${
                          isFilterPhase && !rowRevealed ? "opacity-0" : "opacity-100"
                        } ${
                          stock.tradeAction === "skipped"
                            ? isAlpha ? "text-v7-rust" : "text-red-600 dark:text-red-400"
                            : stock.tradeAction === "sell" || stock.tradeAction === "sold"
                              ? isAlpha ? "text-v7-bronze-dark" : "text-yellow-600 dark:text-yellow-500"
                              : stock.tradeAction === "buy" || stock.tradeAction === "bought"
                                ? isAlpha ? "text-v7-success" : "text-green-600 dark:text-green-500"
                                : isAlpha ? "text-v7-ink/50" : "text-gray-400 dark:text-gray-500"
                        }`}>
                          {formatExclusionTradeLabel(stock.tradeAction)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {snapshot.headline && !isFilterPhase && (
                <div className={`px-4 py-3 text-sm ${isAlpha ? "rounded-v7-panel border border-v7-ink/15 bg-v7-white text-v7-ink-soft" : "rounded-lg border border-gray-200 bg-white text-gray-600 dark:border-rp-separator dark:bg-black dark:text-gray-300"}`}>
                  {snapshot.headline}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {EXCLUSION_PHASE_ORDER.map((step, index) => (
                  <button
                    key={step}
                    type="button"
                    onClick={() => setPhase(step)}
                    aria-current={index === activeStepIndex ? "step" : undefined}
                    className={
                      index === activeStepIndex
                        ? isAlpha
                          ? "rounded-v7-control border border-v7-bronze-dark bg-v7-bronze/10 px-2 py-2 text-center transition-colors hover:bg-v7-bronze/15"
                          : "rounded-lg border border-rp-amber bg-rp-amber/10 px-2 py-2 text-center transition-colors hover:bg-rp-amber/15"
                        : isAlpha
                          ? "rounded-v7-control border border-v7-ink/15 bg-v7-white px-2 py-2 text-center transition-colors hover:border-v7-bronze/40 hover:bg-v7-paper"
                          : "rounded-lg border border-gray-200 bg-white px-2 py-2 text-center transition-colors hover:border-rp-amber/40 hover:bg-gray-50 dark:border-rp-separator dark:bg-black dark:hover:bg-rp-surface"
                    }
                  >
                    <p className={microLabelClass}>
                      Step {index + 1}
                    </p>
                    <p className={`mt-1 text-xs font-semibold ${strongTextClass}`}>
                      {EXCLUSION_PHASE_LABELS[step]}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={`p-4 ${cardClass}`}>
            <p className={`text-sm ${bodyTextClass}`}>
              {mode === "excluded" ? (
                <>
                  With <span className={`font-semibold ${strongTextClass}`}>{EXCLUSION_SYMBOL} excluded</span>, automated trading skips that symbol even when the strategy drifts.
                </>
              ) : (
                <>
                  With <span className={`font-semibold ${strongTextClass}`}>no exclusions</span>, every symbol in the strategy can be bought or sold during automated rebalancing.
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes exclusionExplainerCheckPop {
          0% { transform: scale(0.6); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }

        .exclusion-explainer-check {
          animation: exclusionExplainerCheckPop 0.35s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ContractExclusionsExplainerModal;
